import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Gift, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../services/api';

/**
 * ExitIntentPopup Component
 * Shows a newsletter signup modal when user's mouse moves to exit the browser
 */
const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const hasSeenPopup = useCallback(() => {
    const seen = localStorage.getItem('exit_popup_seen');
    if (!seen) return false;
    
    // Show again after 7 days
    const seenDate = new Date(seen);
    const daysSince = (Date.now() - seenDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSince < 7;
  }, []);

  const markAsSeen = () => {
    localStorage.setItem('exit_popup_seen', new Date().toISOString());
  };

  useEffect(() => {
    // Don't show if already seen recently
    if (hasSeenPopup()) return;

    let timeoutId;
    
    const handleMouseLeave = (e) => {
      // Only trigger when mouse moves to top of viewport (exit intent)
      if (e.clientY <= 10 && !isVisible) {
        // Add slight delay to avoid false triggers
        timeoutId = setTimeout(() => {
          setIsVisible(true);
          markAsSeen();
        }, 100);
      }
    };

    // Add event listener after a delay (don't show immediately on page load)
    const setupTimeout = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000); // Wait 5 seconds before enabling

    return () => {
      clearTimeout(setupTimeout);
      clearTimeout(timeoutId);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isVisible, hasSeenPopup]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await api.subscribeNewsletter(email);
      if (response.status === 'success' || response.status === 'existing') {
        setStatus('success');
        setTimeout(() => {
          setIsVisible(false);
        }, 2500);
      } else {
        throw new Error(response.message || 'Subscription failed');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message || 'Something went wrong. Please try again.');
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="exit-intent-popup"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors z-10"
          data-testid="exit-popup-close"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-[var(--brand)] to-[var(--brand-dark)] px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
            <Gift className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Wait! Don&apos;t Miss Out
          </h2>
          <p className="text-white/90 text-sm">
            Get 10% off your first order when you join our family!
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {status === 'success' ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text)] mb-2">
                You&apos;re In!
              </h3>
              <p className="text-[var(--muted)] text-sm">
                Check your inbox for your exclusive discount code.
              </p>
            </div>
          ) : (
            <>
              <p className="text-[var(--muted)] text-center mb-6">
                Join 10,000+ families building better habits. Plus, get exclusive deals and parenting tips!
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 py-6 text-base"
                    disabled={status === 'loading'}
                    data-testid="exit-popup-email"
                  />
                </div>

                {status === 'error' && errorMessage && (
                  <p className="text-red-500 text-sm text-center">{errorMessage}</p>
                )}

                <Button
                  type="submit"
                  className="w-full py-6 text-base font-semibold"
                  style={{ background: 'var(--brand)' }}
                  disabled={status === 'loading'}
                  data-testid="exit-popup-submit"
                >
                  {status === 'loading' ? (
                    'Subscribing...'
                  ) : (
                    <>
                      Get My 10% Discount
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-xs text-[var(--muted)] text-center mt-4">
                No spam, ever. Unsubscribe anytime.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
